import { Input, Form, InputNumber, DatePicker, Radio, Checkbox, Switch, Flex } from "antd";
import TextArea from "antd/es/input/TextArea"
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { getLabel } from "../../../utils/getNameFromEmail";
import { QuestionCircleOutlined } from "@ant-design/icons";
import ParentSelector from "./ParentSelector";
import UserRoleDropdown from "../../UserRoleDropdown";
import moment from "moment";
import dayjs from "dayjs";
import { toDatePickerValue } from "../../../utils/safeDate.js";
import { getFieldChildren, isRowTranslatableField, resolveFieldTranslation } from "../../../utils/masterTranslations.js";
import { formatWithThousandSeparators, stripThousandSeparators } from "../../../utils/numberFormatter.js";


const AddDataModal = (props) => {
    const { formItems, masterList, handleItemChange, formValues, masterData, form, isEdit } = props
    const { t } = useTranslation();
    // When translating, only the columns whose stored value is display text are
    // editable — everything else is an id, a number or a date and is shared
    // across languages. The canonical text is shown as reference, never loaded
    // into an input, so a translator cannot overwrite it.
    const { translationMode = false, translationLanguage, canonicalRecord = {} } = props

    useEffect(() => {
        if (isEdit || translationMode || !form) return
        const defaults = masterData?.defaultValues
        if (!defaults || typeof defaults !== "object" || Object.keys(defaults).length === 0) return

        const idToLabel = {}
        const collect = (fields) => {
            (fields || []).forEach(field => {
                if (field?.id) idToLabel[field.id] = field.label
                if (Array.isArray(field?.children)) collect(field.children)
            })
        }
        collect(masterData?.fields)

        const mapped = {}
        Object.keys(defaults).forEach(key => {
            const label = idToLabel[key] || key
            mapped[label] = defaults[key]
        })

        if (Object.keys(mapped).length > 0) {
            form.setFieldsValue(mapped)
        }
    }, [isEdit, translationMode, masterData?.defaultValues, masterData?.fields, form])

    // The column header a translator sees, in the language being edited
    const localizedLabel = (item) => resolveFieldTranslation(item, translationLanguage)?.label || item.label

    // `name` stays the canonical label — it is the key the row is stored under
    const getTranslationElement = (item, parentLabel = "") => {
        const canonicalValue = parentLabel
            ? canonicalRecord?.[parentLabel]?.[item.label]
            : canonicalRecord?.[item.label]
        const isLongText = item.formType === "textAreaForm" || item.formType === "textArea"
        const label = (
            <Flex vertical>
                <span>{localizedLabel(item)}</span>
                <span className="text-color-secondary" style={{ fontSize: "12px", fontWeight: 400 }}>
                    {canonicalValue || "—"}
                </span>
            </Flex>
        )
        return (
            <Form.Item
                key={parentLabel ? `${parentLabel}-${item.label}` : item.label}
                label={label}
                name={parentLabel ? [parentLabel, item.label] : item.label}
            >
                {isLongText
                    ? <TextArea rows={item.maxHeight || 2} placeholder={t("Enter translation")} />
                    : <Input placeholder={t("Enter translation")} />}
            </Form.Item>
        )
    }

    const renderTranslationForm = () => {
        const sections = formItems.map(item => {
            const children = getFieldChildren(item)
            if (children) {
                const translatableChildren = children.filter(isRowTranslatableField)
                if (translatableChildren.length === 0) return null
                return (
                    <div key={item.label}>
                        <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "5px" }}>{localizedLabel(item)}:</div>
                        {translatableChildren.map(child => getTranslationElement(child, item.label))}
                    </div>
                )
            }
            return isRowTranslatableField(item) ? getTranslationElement(item) : null
        }).filter(Boolean)

        if (sections.length === 0) {
            return <div className="text-color-secondary">
                {t("This table has no text columns to translate. Dropdown, number and date columns are shared across languages.")}
            </div>
        }
        return sections
    }

    const handleChange = (label, val, parentLabel) => {
        if (handleItemChange) {
            handleItemChange(label, val, parentLabel)
        }
    }

    const onSelect = (item, values) => {
        if (form) {
            form.setFieldValue(item.label, values)
        }
    }

    const getElement = (item, parentLabel = "") => {
        let itemName = item.label;
        let itemLabel = item.label
        const disabled = item.readOnly ? true : false
        const help = item.tooltip ? { title: item.helpText, icon: <QuestionCircleOutlined /> }
            : undefined;
        const nonTextFormTypes = ["dropdownField", "numberForm", "userForm", "dateForm", "radioField", "checkboxField", "multipleChoiceField", "switchForm"]
        let rules = item.mandatory ? [{ required: true, whitespace: !nonTextFormTypes.includes(item.formType), message: item.label + " is required" }] : undefined

        const options = (Array.isArray(item.options) ? item.options : []).map((opt) => ({
            label: opt?.label ?? opt?.value ?? "",
            value: opt?.value ?? opt?.label ?? "",
        }))

        if (item.formType === "inputForm") {
            return <Form.Item key={item.label} label={itemLabel} name={parentLabel ? [parentLabel, itemName] : itemName} tooltip={help} rules={rules}>
                <Input placeholder={item.placeholder} disabled={disabled} onChange={(e) => handleChange(itemName, e.target.value, parentLabel)} />
            </Form.Item>
        } else if (item.formType === "dropdownField") {
            return <ParentSelector tooltip={help} rules={rules} originalLabel={item.label} name={itemName} {...item} label={itemLabel} disabled={disabled} masterList={masterList} formValues={formValues} masterData={masterData} contentLang={translationLanguage} />

        } else if (item.formType === "numberForm") {
            return <Form.Item key={item.label} label={itemLabel} name={itemName} tooltip={help} rules={rules}>
                <InputNumber
                    placeholder={item.placeholder}
                    disabled={disabled}
                    style={{ width: "100%" }}
                    formatter={(value) => formatWithThousandSeparators(value)}
                    parser={(value) => stripThousandSeparators(value)}
                />
            </Form.Item>
        } else if (item.formType === "userForm") {
            return <Form.Item key={item.label} label={itemLabel} name={itemName} tooltip={help} rules={rules}>
                <UserRoleDropdown getSpaceUsers={false} mode="SINGLE"
                    placeholder={t('Select user')}
                    selectedOptions={formValues[item.label] ? {
                        "user": formValues[item.label]?.user || [],
                        "role": [],
                        "group": []
                    } : { user: [], role: [], group: [] }}
                    onSelect={(value) => onSelect(item, value)}
                    tabsRequired={["user"]} />
            </Form.Item>
        } else if (item.formType === "dateForm") {
            return <Form.Item key={item.label} label={itemLabel} name={itemName} tooltip={help} rules={rules}
                getValueProps={(value) => ({ value: (value !== "N/A" && value !== "NA") ? toDatePickerValue(value) : null })}
            >
                <DatePicker
                    style={{ width: '100%' }}
                    placeholder={t('Select date...')}
                />
            </Form.Item>
        } else if (item.formType === "radioField") {
            return <Form.Item key={item.label} label={itemLabel} name={itemName} tooltip={help} rules={rules}>
                <Radio.Group options={options} disabled={disabled} />
            </Form.Item>
        } else if (item.formType === "checkboxField" || item.formType === "multipleChoiceField") {
            return <Form.Item key={item.label} label={itemLabel} name={itemName} tooltip={help} rules={rules}>
                <Checkbox.Group options={options} disabled={disabled} />
            </Form.Item>
        } else if (item.formType === "switchForm") {
            return <Form.Item key={item.label} label={itemLabel} name={itemName} tooltip={help} rules={rules} valuePropName="checked">
                <Switch disabled={disabled} size="small" />
            </Form.Item>
        } else {
            return <Form.Item key={item.label} label={itemLabel} name={itemName} tooltip={help} rules={rules}>
                <TextArea placeholder={item.placeholder} rows={item.maxHeight || 2} disabled={disabled} />
            </Form.Item>
        }
    }

    if (translationMode) {
        return <>{renderTranslationForm()}</>
    }

    return (
        <>
            {
                formItems.map(item => {
                    let itemName = item.label;
                    return <>
                        {
                            item.formType === "section" ?
                                <>
                                    <div style={{ fontSize: "16px", fontWeight: "600", marginBottom: "5px" }}>{item.label}:</div>
                                    {
                                        item.children?.length && item.children.map(child => {
                                            return getElement({ ...child }, itemName)
                                        })
                                    }
                                </>
                                : getElement(item)

                        }
                    </>
                })
            }
        </>
    )
}

export default AddDataModal
